/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useMemo, useRef} from 'react';
import isNil from 'lodash.isnil';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {navigateToGoogleDirections} from '@ion/dls/src/maps/helpers/globeUtils';
import {LayerSettingsProps} from './LayerSettings';

// defines the prop definitions for the OperationalLayers
export type OperationalLayersProps = LayerSettingsProps & {
  view?: __esri.MapView,
  operationalLayersProps?: any,
  findNearby?: Function,
  initializeOperationalLayers?: Function,
  layers?: string[],
  isTracking2Page?: boolean
};

// Popup content style
const styles = {padding: '10px 5px 10px 5px'};

// Defines the Actions Ids for the popup template
enum ActionsId {
  findNearby = 'findNearby',
  navegateTo = 'navegateTo'
};

/*
* Defines a method to check a layer exists in array of layers
*/
const hasLayer = (layers: __esri.Layer[], title: string): boolean => {
  return !!layers?.find(layer => layer.title === title);
};

/**
 * Defines the OperationalLayers which manages rendering a layer containing individual
 * Assets within a DLS Map.
 *
 * ```typescript
 *   import {ResponsiveBaseMapLayer, OperationalLayers} from './layers';
 *   const components: any = [ResponsiveBaseMapLayer]
 *   components.push(OperationalLayers);
 *
 * ```
 */
export const OperationalLayers = ({
  view,
  map,
  operationalLayersProps: {
    operationalLayers,
    findNearby,
    isLayerListUpdate
  } = {},
  layers: visibleLayers,
  isTracking2Page = false
}: OperationalLayersProps) => {
  const currentlayers = useRef(operationalLayers);
  const isMounted     = useHasMounted();
  const padding       = `padding: ${styles.padding}`;

  // Defines the Action for the popup template
  const popupActions = useMemo(() => [{
    id    : ActionsId.navegateTo,
    title : translate('common:navigateTo')
  }], []);

  useEffect(() => {
    if (isMounted && !isTracking2Page) {
      const {popup} = view;
      popup?.watch('selectedFeature', (graphic: __esri.Graphic) => {
        if (currentlayers?.current?.length && graphic) {
          const {layer} = graphic || {};
          if (hasLayer(currentlayers.current, layer?.title)) {
            const {popupTemplate} = layer || {};
            if (popupTemplate) {
              const text = popupTemplate?.content[0]?.text;
              if (text && !text?.startsWith(`<div style='${padding}'>`)) {
                popupTemplate.content[0].text = `<div style='${padding}'>
                                                  ${text}
                                                <div>`;
              }
              popupTemplate.actions = popupActions;
            }
          }
        }
      });

      popup?.on('trigger-action', (event) => {
        const {id}              = event?.action || {};
        const {selectedFeature} = popup.viewModel || {};
        if (hasLayer(currentlayers?.current, selectedFeature?.layer?.title)) {
          const {geometry}          = selectedFeature || {};
          selectedFeature.latitude  = geometry?.latitude;
          selectedFeature.longitude = geometry?.longitude;
          if (id === ActionsId.navegateTo) {
            navigateToGoogleDirections(selectedFeature);
          }
          if (id === ActionsId.findNearby) {
            findNearby(selectedFeature);
          }
        }
      });
    }
  }, [isMounted, isTracking2Page]);

  useEffect(() => {
    currentlayers.current = operationalLayers;
  }, [operationalLayers]);

  useEffect(() => {
    setLayerVisibility(visibleLayers);
  }, [visibleLayers]);

  useEffect(() => {
    setSublayersVisibility(operationalLayers);
  }, [operationalLayers, isLayerListUpdate]);

  /**
 * @private
 *
 * @function setLayerVisibility
 */
  function setLayerVisibility(visibleLayers: Array<string>) {
    const {items} = map?.allLayers;
    operationalLayers?.forEach(({title}) => {
      const layer = items?.find(({title: _title}) => _title === title);
      if (layer) {
        layer.visible = visibleLayers?.includes(title);
      }
    });
  };

  /**
 * @private
 *
 * @function setSublayersVisibility
 */
  function setSublayersVisibility(operationalLayers: Array<object>) {
    const {items} = map?.allLayers;
    operationalLayers?.forEach(({title, layers, opacity}) => {
      const layer = items?.find(({title: _title}) => _title === title);
      if (layer) {
        layer.opacity = opacity;
        layers?.forEach(({id, visibility, defaultVisibility}) => {
          const sublayer = layer?.findSublayerById(parseInt(id));
          if (sublayer) {
            sublayer.visible = !isNil(defaultVisibility) ? defaultVisibility : visibility;
          }
        });
      }
    });
  };

  return null;
};

export default OperationalLayers;
